import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore, shallowEqual } from 'react-redux';
import { useUser } from '../useUser';
import { useWhere } from '../useWhere';
import { getTimeParams } from '../../util/Restaurant';
import * as orderActions from '../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { toggleAddress } from '../../redux/legacy/reducers/ducks/legacy/MainDuck';
import { setSelectedAddress } from '../../redux/legacy/reducers/ducks/new/Address/AddressReducer';
import { formatCartForApi } from '../../util/Helpers';
import { deliveryAddressError } from '../../util/deliveryAddress';
import resolveDeliveryAddress from '../../util/deliveryAddressService';
import Notification from '../../components/layout/Notification';
import { getOrderTiming } from '../../util/orderTiming';
import { createCheckoutAttemptContext } from '../../util/checkoutContext';
import {
  checkoutProgress,
  nextCheckoutAttempt,
} from '../../util/checkoutProgress';

function useSetCart(needToStart) {
  const dispatch = useDispatch();
  const store = useStore();
  const { userData } = useUser();
  // Restore legacy data only when there is no selected Redux destination.
  const { selectedAddress: address } = useWhere();
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [addressNeededCount, setAddressNeededCount] = useState(0);
  const [setCartFailedCount, setSetCartFailedCount] = useState(0);
  const active = useRef(false);
  const activeOperation = useRef(null);
  const submitted = useRef(false);
  const waitingForCart = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeOperation.current?.unsubscribe?.();
    };
  }, []);
  const { authLoading, time, response } = useSelector(
    (state) => ({
      authLoading: state.authorization?.isLoading,
      time: state.time.selectedTime,
      response: state.order.setCartResponse,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!needToStart) {
      activeOperation.current?.unsubscribe?.();
      submitted.current = false;
      active.current = false;
      activeOperation.current = null;
      waitingForCart.current = false;
      setCheckoutStarted(false);
    }
    if (
      !needToStart ||
      !userData?.id ||
      authLoading ||
      active.current ||
      submitted.current
    )
      return;
    active.current = true;
    const operation = { id: nextCheckoutAttempt() };
    activeOperation.current = operation;
    submitted.current = true;
    setCheckoutStarted(false);
    const prepare = async () => {
      const fail = (message, outcome = 'blocked') => {
        if (
          !mounted.current ||
          activeOperation.current !== operation ||
          operation.settled
        )
          return;
        operation.settled = true;
        operation.unsubscribe?.();
        active.current = false;
        waitingForCart.current = false;
        setCheckoutStarted(false);
        setSetCartFailedCount((count) => count + 1);
        checkoutProgress('cart-prepare', operation.id, outcome);
        Notification.error(message, { toastId: 'checkout-progress' });
      };
      operation.fail = fail;
      const initialTiming = getOrderTiming(store.getState());
      if (initialTiming.requiresConfirmation) {
        fail(
          'Please choose and confirm your order time, then try checkout again.'
        );
        dispatch(orderActions.setCheckoutContinuation(false));
        return;
      }
      const selected = store.getState().address.selectedAddress;
      const checkoutContext = createCheckoutAttemptContext(store.getState());
      operation.getContext = checkoutContext;
      const context = checkoutContext(store.getState());
      operation.context = context;
      operation.unsubscribe = store.subscribe(() => {
        if (
          checkoutContext(store.getState()) !== context ||
          getOrderTiming(store.getState()).requiresConfirmation
        ) {
          fail(
            'Your order details changed. Review your cart and try checkout again.',
            'cancelled'
          );
        }
      });
      checkoutProgress('cart-prepare', operation.id, 'pending');
      const isCurrent = () =>
        mounted.current &&
        activeOperation.current === operation &&
        checkoutContext(store.getState()) === context &&
        !operation.settled &&
        !store.getState().authorization?.isLoading;
      let resolved = selected;
      const initialType = store.getState().order.type?.value;
      try {
        if (initialType === 'delivery') {
          resolved = await resolveDeliveryAddress(
            selected,
            userData.id,
            isCurrent
          );
          const current = store.getState();
          if (
            !isCurrent() ||
            current.main.user?.id !== userData.id ||
            !mounted.current ||
            current.order.type?.value !== initialType
          ) {
            throw new Error('Order context changed');
          }
          dispatch(setSelectedAddress(resolved));
        }
      } catch (error) {
        if (activeOperation.current !== operation || operation.settled) return;
        if (!mounted.current || checkoutContext(store.getState()) !== context) {
          fail(
            'Your order details changed. Review your cart and try checkout again.',
            'cancelled'
          );
          return;
        }
        operation.unsubscribe?.();
        Notification.error(deliveryAddressError(error), {
          toastId: 'delivery-address-confirm',
        });
        if (
          error?.status === 401 ||
          error?.response?.status === 401 ||
          store.getState().main.user?.id !== userData.id
        ) {
          // Preserve the cart's session-expired recovery path. An auth failure
          // is not a request for the customer to re-enter their destination.
          setSetCartFailedCount((count) => count + 1);
          active.current = false;
          return;
        }
        dispatch(orderActions.setAddressRequired(true));
        dispatch(toggleAddress(true));
        setAddressNeededCount((count) => count + 1);
        active.current = false;
        return;
      }
      const current = store.getState();
      const currentTiming = getOrderTiming(current);
      if (
        operation.settled ||
        checkoutContext(current) !== context ||
        currentTiming.requiresConfirmation ||
        currentTiming.key !== initialTiming.key
      ) {
        fail(
          'Your order details changed. Review your cart and try checkout again.',
          'cancelled'
        );
        return;
      }
      waitingForCart.current = true;
      dispatch(orderActions.setCartResponse(null));
      dispatch(
        orderActions.setCart({
          payloadCartData: { cart: current.order.cartItems },
          apiData: {
            client: current.restaurant.selectedRestaurantId,
            data: {
              mode: current.order.type.value,
              ...getTimeParams(current.time.selectedTime),
              cart: formatCartForApi(current.order.cartItems),
              ...(initialType === 'delivery'
                ? { address: resolved.address_id }
                : {}),
            },
          },
          userProfile: userData,
          isCheckoutStart: true,
          attemptId: operation.id,
        })
      );
    };
    prepare().catch(() =>
      operation.fail?.('Could not prepare checkout. Please try again.')
    );
  }, [needToStart, userData, authLoading, time, address]);

  useEffect(() => {
    if (!active.current || !waitingForCart.current || !response) return;
    const operation = activeOperation.current;
    if (response.attemptId !== operation?.id) return;
    if (
      operation.context !== operation.getContext(store.getState()) ||
      getOrderTiming(store.getState()).requiresConfirmation
    ) {
      operation.fail(
        'Your order details changed. Review your cart and try checkout again.',
        'cancelled'
      );
      return;
    }
    active.current = false;
    waitingForCart.current = false;
    if (response.error) {
      operation.unsubscribe?.();
      operation.settled = true;
      checkoutProgress('cart-consume', operation.id, 'failed');
      Notification.error(
        'Could not prepare checkout. Please review your cart and try again.',
        { toastId: 'checkout-progress' }
      );
      setCheckoutStarted(false);
      setSetCartFailedCount((count) => count + 1);
    } else {
      checkoutProgress('cart-consume', operation.id, 'success');
      setCheckoutStarted(true);
    }
  }, [response]);

  const isCheckoutCurrent = () => {
    const operation = activeOperation.current;
    const current =
      !!operation &&
      !operation.settled &&
      operation.context === operation.getContext?.(store.getState()) &&
      !getOrderTiming(store.getState()).requiresConfirmation;
    if (current) checkoutProgress('cart-navigation', operation.id, 'success');
    return current;
  };
  return [
    checkoutStarted,
    addressNeededCount,
    setCartFailedCount,
    isCheckoutCurrent,
  ];
}

export default useSetCart;
